import { type Cap, useAccess } from '@/auth/caps';
import { destinationPath } from '@/auth/destination';
import { useAuth } from '@/auth/store';
import { useTenant } from '@/shell/tenant';
import { Toast } from '@/ui';
import { Navigate, Outlet } from 'react-router-dom';

/** The shell renders only on a session with a person; a token alone is not one (store.ts readSession). */
export function RequireAuth() {
  const ok = useAuth((s) => s.token != null && s.user != null);
  // a person with no company holds the narrow identity token (role null): the account
  // pages are theirs, the shell is not (TF-ACCOUNT-ACCESS, 2026-09-15)
  const identityOnly = useAuth((s) => s.user != null && s.user.role == null);
  // where the site sent them: their demo or their own company (plan 0028 slice 1)
  if (identityOnly) return <Navigate to={destinationPath()} replace />;
  // the toast lives here, not in the shell: /oppsett and /abonnement sit outside the shell and
  // their errors went unseen (onboarding beta test, 2026-11-05)
  return ok ? (
    <>
      <Outlet />
      <Toast />
    </>
  ) : (
    <Navigate to="/login" replace />
  );
}
/** The account pages: any signed-in person, with or without a company. */
export function RequireIdentity() {
  const ok = useAuth((s) => s.token != null && s.user != null);
  return ok ? <Outlet /> : <Navigate to="/login" replace />;
}
/**
 * A screen the role lacks the capability for sends the person home instead of showing 403
 * alerts. What the role may do comes from the company, so this waits for the company to
 * answer: bouncing on a permission list that has not arrived would send everybody home for
 * an instant on every cold load (custom roles, 2026-09-16).
 */
export function Guard({ cap, children }: { cap: Cap; children: React.ReactElement }) {
  const { ready, can } = useAccess();
  if (!ready) return null;
  return can(cap) ? children : <Navigate to="/min" replace />;
}
export function Home() {
  const { ready, can } = useAccess();
  const { tenant } = useTenant();
  if (!ready) return null;
  // a new company's administrator finishes the setup first, and comes back to it (plan 0028 slice 3)
  if (tenant?.setup_step && tenant.me?.is_admin) return <Navigate to="/oppsett" replace />;
  // Vaktplan is the first page after login for whoever may see it (owner, 2026-10-18, replacing
  // Hjem with planning on from 2026-09-21); with planning off it is the week grid «Uka» (#118)
  if (can('vaktplan.se')) return <Navigate to="/vaktplan" replace />;
  return <Navigate to={can('timeliste.se') ? '/hjem' : '/min'} replace />;
}
